import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ShieldCheck, ArrowRight, Award, Network, TrendingUp, Search, QrCode, Loader2, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, resolveFileUrl } from "@/lib/api";
import { AnimatedCounter } from "@/components/ui-custom/AnimatedCounter";
import { RevealOnScroll, RevealStagger, staggerItem } from "@/components/ui-custom/RevealOnScroll";
import { SectionHeading } from "@/components/ui-custom/SectionHeading";
import { DivisionMap } from "@/components/DivisionMap";
import { QRScannerDialog } from "@/components/QRScannerDialog";
import { extractVerifyToken } from "@/lib/qr";
import { COMMITTEE_PHOTO_MAP } from "@/lib/committeePhotos";

const EASE = [0.22, 1, 0.36, 1];

const PILLARS = [
  { icon: Network, title: "Perpaduan Profesion", desc: "Menyatukan pengawal peribadi dan pengamal keselamatan profesional di seluruh Sarawak dalam satu suara industri." },
  { icon: Award, title: "Piawaian Profesional", desc: "Memperkukuh disiplin, etika dan kompetensi profesional anggota pengawal peribadi." },
  { icon: ShieldCheck, title: "Direktori Disahkan", desc: "Sistem pengesahan ahli digital yang telus dan boleh disemak oleh orang awam." },
  { icon: TrendingUp, title: "Pembangunan Kapasiti", desc: "Latihan berterusan untuk membangunkan pengawal peribadi yang profesional dan berdaya saing." },
];

const HERO_LINE_1 = "MEMPERKASA PROFESION";
const HERO_LINE_2_PREFIX = "PENGAWAL PERIBADI ";
const HERO_LINE_2_ACCENT = "SARAWAK";

function VerifyWidget() {
  const navigate = useNavigate();
  const [num, setNum] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!num.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await api.get(`/public/verify`, { params: { member_number: num.trim().toUpperCase() } });
      setResult(r.data);
    } catch {
      setError("Ahli tidak dijumpai. Sila semak No. Ahli.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleVerify} className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="home-verify-form">
        <Input
          value={num}
          onChange={(e) => setNum(e.target.value)}
          placeholder="Masukkan No. Ahli, contoh: BGS-2024-101"
          className="h-12 bg-white/5 border-white/15 text-white placeholder:text-white/30 rounded-full px-5"
          data-testid="home-verify-input"
        />
        <Button type="submit" disabled={loading} className="h-12 px-7 rounded-full bg-piks-gold hover:bg-piks-gold/90 text-piks-black font-bold shrink-0" data-testid="home-verify-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />} SEMAK SEKARANG
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setScannerOpen(true)}
          className="h-12 px-5 rounded-full border-white/20 text-white/80 hover:text-piks-gold hover:border-piks-gold/50 bg-transparent shrink-0"
          data-testid="home-verify-scan-qr"
        >
          <QrCode className="w-4 h-4 mr-2" /> SCAN QR
        </Button>
      </form>

      <QRScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onResult={(text) => {
          setScannerOpen(false);
          navigate(`/semak/${extractVerifyToken(text)}`);
        }}
      />


      {error && <p className="text-piks-red text-sm mb-4" data-testid="home-verify-error">{error}</p>}

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl p-6 border ${result.verified ? "border-piks-gold/40 shadow-[0_0_25px_rgba(244,196,48,0.15)]" : "border-white/15"} bg-white/5`}
          data-testid="home-verify-result"
        >
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className={`w-5 h-5 ${result.verified ? "text-piks-gold" : "text-white/40"}`} />
            <p className="font-display font-extrabold text-white tracking-wide">{result.verified ? "✓ AHLI DISAHKAN" : "KEAHLIAN TIDAK AKTIF"}</p>
          </div>
          <div className="space-y-2.5 text-sm">
            <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/40">Nama</span><span className="font-semibold text-white">{result.full_name}</span></div>
            <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/40">No. Ahli</span><span className="font-mono font-semibold text-piks-gold">{result.member_number}</span></div>
            <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/40">Kategori</span><span className="font-semibold text-white">{result.category}</span></div>
            <div className="flex justify-between py-2"><span className="text-white/40">Sah Sehingga</span><span className="font-semibold text-white">{result.valid_until ? new Date(result.valid_until).toLocaleDateString("ms-MY") : "-"}</span></div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default function Home() {
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [committee, setCommittee] = useState([]);
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 60]);

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/public/announcements").then((r) => setAnnouncements(r.data.slice(0, 3))).catch(() => {});
    api.get("/public/committee").then((r) => setCommittee(r.data.members?.slice(0, 4) || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* 1. DARK PREMIUM HERO */}
      <section ref={heroRef} className="relative bg-piks-black text-white overflow-hidden min-h-screen flex items-center">
        <motion.div className="absolute inset-0" style={{ y: parallaxY }}>
          <img src="/gallery/hero-air-borneo.jpg" alt="" className="absolute inset-0 w-full h-full object-cover object-[center_28%] lg:scale-[1.15] lg:translate-x-[5%]" />
          <div className="absolute inset-0 bg-piks-black/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-piks-black via-piks-black/45 to-piks-black/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-piks-black via-piks-black/35 to-transparent" />
        </motion.div>
        <div className="absolute inset-0 pattern-dots-animated opacity-[0.15]" />
        <img src="/piks-logo.png?v=7" alt="" className="crest-watermark absolute -right-20 -top-10 w-[420px] object-contain select-none hidden lg:block" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 sm:pt-24 w-full">
          <div className="max-w-4xl">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="section-eyebrow mb-6"
              data-testid="hero-eyebrow"
            >
              PERSATUAN PENGAWAL PERIBADI SARAWAK
            </motion.p>
            <h1 className="font-display text-[clamp(1.6rem,3.6vw,3rem)] font-extrabold leading-[1.15] tracking-tight mb-6" data-testid="hero-headline">
              <motion.span
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0, ease: EASE }}
                className="block"
              >
                {HERO_LINE_1}
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.12, ease: EASE }}
                className="block"
              >
                {HERO_LINE_2_PREFIX}<span className="text-piks-gold">{HERO_LINE_2_ACCENT}</span>
              </motion.span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="text-base sm:text-lg text-white/70 leading-relaxed max-w-xl mb-10"
            >
              Menyatukan pengawal peribadi profesional, memperkukuh standard industri dan membina komuniti perlindungan yang lebih dipercayai di Sarawak.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="flex flex-wrap items-center gap-4"
            >
              <Link to="/tuntut-keahlian" data-testid="hero-tuntut-ahli-btn">
                <Button size="lg" className="rounded-full h-12 px-7 bg-piks-red hover:bg-piks-red/90 text-white font-bold text-sm tracking-wide shadow-xl shadow-piks-red/20 group">
                  TUNTUT AHLI <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/semak" data-testid="hero-semak-ahli-btn">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 border-piks-gold/50 text-piks-gold hover:bg-piks-gold hover:text-piks-black bg-transparent font-bold text-sm tracking-wide">
                  SEMAK AHLI
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.72 }}
              className="mt-4"
            >
              <Link to="/kursus" data-testid="hero-daftar-kursus-btn">
                <Button size="lg" variant="outline" className="rounded-full h-11 px-6 border-white/30 text-white hover:bg-white/10 hover:border-white/50 bg-transparent font-bold text-sm tracking-wide group">
                  <GraduationCap className="w-4 h-4 mr-2" /> DAFTAR KURSUS <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. WHITE AUTHORITY STATISTICS */}
      <section className="bg-white py-16 sm:py-20 border-b border-piks-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RevealStagger className="grid grid-cols-2 sm:grid-cols-4 gap-8" staggerDelay={0.1}>
            {[
              { key: "verified_members", label: "Ahli Disahkan", testid: "stat-verified-members" },
              { key: "active_members", label: "Ahli Aktif", testid: "stat-active-members" },
              { key: "programs_count", label: "Program Dianjurkan", testid: "stat-programs" },
              { key: "registered_companies", label: "Syarikat / Rakan Industri", testid: "stat-companies" },
            ].map((s) => (
              <motion.div key={s.key} variants={staggerItem} data-testid={s.testid}>
                <p className="font-display text-4xl sm:text-5xl font-extrabold text-piks-black">
                  {stats ? <AnimatedCounter value={stats[s.key] || 0} suffix="+" /> : "—"}
                </p>
                <p className="text-xs sm:text-sm text-piks-muted uppercase tracking-wider mt-2">{s.label}</p>
              </motion.div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* 3. BLACK VERIFIED MEMBER / VERIFICATION SECTION */}
      <section className="bg-piks-black text-white py-20 sm:py-28 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <RevealOnScroll>
            <SectionHeading number="01" title="Semakan Awam" light />
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mb-4">SEMAK AHLI BODYGUARD SARAWAK</h2>
            <p className="text-white/50 mb-10 max-w-lg">
              Sahkan status keahlian mana-mana ahli PPPS secara telus menggunakan No. Ahli atau kod QR pada kad ahli digital mereka.
            </p>
            <VerifyWidget />
          </RevealOnScroll>
        </div>
      </section>

      {/* 4. WHITE ABOUT / MISSION */}
      <section className="bg-white py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <RevealOnScroll>
            <SectionHeading number="02" title="Tentang Kami" />
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold text-piks-black mb-5">
              Satu Komuniti. Satu Standard. Satu Profesion.
            </h2>
            <p className="text-piks-muted leading-relaxed mb-8 max-w-md">
              Persatuan Pengawal Peribadi Sarawak (PPPS) menyatukan pengawal peribadi dan pengamal keselamatan profesional
              di seluruh Sarawak, memperkukuh imej, disiplin dan standard industri pengawalan peribadi sejak 2015.
            </p>
            <Link to="/tentang">
              <Button variant="outline" className="rounded-full h-11 px-6 border-piks-black text-piks-black hover:bg-piks-black hover:text-white font-semibold text-sm group" data-testid="home-tentang-cta">
                Ketahui Lebih Lanjut <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </RevealOnScroll>
          <RevealOnScroll delay={0.1} className="grid grid-cols-2 gap-4">
            {PILLARS.map((p) => (
              <div key={p.title} className="p-5 rounded-xl bg-piks-soft-neutral border border-piks-border hover:border-piks-gold/60 hover:-translate-y-1 transition-all duration-300" data-testid={`pillar-${p.title.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="w-9 h-9 rounded-lg bg-piks-black flex items-center justify-center mb-4">
                  <p.icon className="w-4 h-4 text-piks-gold" />
                </div>
                <h3 className="font-bold text-piks-black text-sm mb-1.5">{p.title}</h3>
                <p className="text-xs text-piks-muted leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </RevealOnScroll>
        </div>
      </section>

      {/* 5. DARK DIVISION MAP */}
      <DivisionMap byDaerah={stats?.by_daerah} />

      {/* 6. SARAWAK RED STATEMENT STRIP */}
      <section className="bg-piks-red text-white py-14 sm:py-16 relative overflow-hidden">
        <div className="diagonal-accent absolute right-10 top-1/2 -translate-y-1/2 opacity-30 scale-150">
          <span className="!bg-white" /><span className="!bg-piks-black" /><span className="!bg-piks-gold" />
        </div>
        <RevealOnScroll className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="font-display text-xl sm:text-2xl lg:text-3xl font-extrabold leading-snug">
            PROFESSIONAL PROTECTION. SARAWAK IDENTITY. TRUSTED PEOPLE.
          </p>
        </RevealOnScroll>
      </section>

      {/* 7. IVORY LEADERSHIP PREVIEW */}
      {committee.length > 0 && (
        <section className="bg-piks-ivory py-20 sm:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealOnScroll className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <SectionHeading number="03" title="Kepimpinan" />
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-piks-black">Ahli Majlis Kerja Tertinggi</h2>
              </div>
              <Link to="/kepimpinan" className="text-sm font-semibold text-piks-black hover:text-piks-red flex items-center gap-1 group" data-testid="home-kepimpinan-link">
                Lihat Semua <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </RevealOnScroll>
            <RevealStagger className="grid grid-cols-2 sm:grid-cols-4 gap-6" staggerDelay={0.08}>
              {committee.map((m) => (
                <motion.div key={m.id} variants={staggerItem} className="group text-center" data-testid={`home-leader-${m.id}`}>
                  <div className="relative w-full aspect-[3/4] rounded-xl overflow-hidden mb-3 transition-transform duration-300 group-hover:-translate-y-1 shadow-sm bg-white">
                    {COMMITTEE_PHOTO_MAP[m.display_name] ? (
                      <img src={COMMITTEE_PHOTO_MAP[m.display_name]} alt={m.display_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="font-display text-3xl text-piks-black">{m.display_name?.[0]}</span>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-piks-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                  </div>
                  <p className="font-semibold text-piks-black text-sm">{m.display_name}</p>
                  <p className="text-xs text-piks-red font-medium">{m.position}</p>
                </motion.div>
              ))}
            </RevealStagger>
          </div>
        </section>
      )}

      {/* 8. WHITE PROGRAM / TRAINING */}
      {announcements.length > 0 && (
        <section className="bg-white py-20 sm:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <RevealOnScroll className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <SectionHeading number="04" title="Terkini" />
                <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-piks-black">Pengumuman & Berita</h2>
              </div>
              <Link to="/berita" className="text-sm font-semibold text-piks-black hover:text-piks-red flex items-center gap-1 group">Lihat Semua <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></Link>
            </RevealOnScroll>
            <RevealStagger className="grid sm:grid-cols-3 gap-6" staggerDelay={0.1}>
              {announcements.map((a) => (
                <motion.div key={a.id} variants={staggerItem} className="rounded-xl bg-piks-soft-neutral border border-piks-border hover:border-piks-gold/50 hover:-translate-y-1 transition-all duration-300 overflow-hidden" data-testid={`announcement-card-${a.id}`}>
                  {a.image_url && (
                    <div className="w-full h-40 overflow-hidden">
                      <img src={resolveFileUrl(a.image_url)} alt={a.title} className="w-full h-full object-cover" data-testid={`announcement-card-image-${a.id}`} />
                    </div>
                  )}
                  <div className="p-6">
                    <p className="text-[11px] text-piks-muted mb-2">{new Date(a.published_at).toLocaleDateString("ms-MY")}</p>
                    <h3 className="font-semibold text-piks-black mb-2 leading-snug">{a.title}</h3>
                    <p className="text-sm text-piks-muted leading-relaxed line-clamp-3">{a.body}</p>
                  </div>
                </motion.div>
              ))}
            </RevealStagger>
          </div>
        </section>
      )}

      {/* 9. BLACK MEMBER JOIN CTA */}
      <section className="bg-piks-black text-white relative overflow-hidden">
        <img src="/piks-logo.png?v=7" alt="" className="crest-watermark absolute -left-16 -bottom-16 w-72 object-contain select-none" />
        <RevealOnScroll className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center relative z-10">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-4">Sedia Untuk Menyertai Bodyguard Sarawak?</h2>
          <p className="text-white/60 mb-8 max-w-lg mx-auto">Jadilah sebahagian daripada komuniti pengawal peribadi Sarawak yang profesional, berdisiplin dan berintegriti.</p>
          <Link to="/tuntut-keahlian">
            <Button size="lg" className="rounded-full h-12 px-8 bg-piks-gold hover:bg-piks-gold/90 text-piks-black font-bold text-sm tracking-wide" data-testid="home-cta-join-btn">TUNTUT AHLI</Button>
          </Link>
        </RevealOnScroll>
      </section>
    </div>
  );
}
